import { Button } from "../../../../shadcn/components/ui/button";
import { FolderSearch } from "lucide-react";
import { PropertyListCardContainer } from "./PropertyListCardContainer";
import { useView } from "app/components/propertiesPage/context/ViewContext";
import { PropertyDataInterface } from "./PropertyCardGridView";
import { useState } from "react";
import { normalizeUrl } from "services";

interface PropertyListViewWrapperPropsInterface {
  allPropertiesData: any;
  isFetchingNextPage: boolean;
  isFilterOpen: boolean;
  onPropertyClick?: (property: PropertyDataInterface) => void;
  onRequestImagesClick?: (property: PropertyDataInterface) => void;
  propertyType: "auction-properties" | "private-properties";
}

export const PropertyListViewWrapper: React.FC<
  PropertyListViewWrapperPropsInterface
> = ({
  allPropertiesData,
  isFetchingNextPage = false,
  isFilterOpen = false,
  onPropertyClick = () => {},
  onRequestImagesClick = () => {},
  propertyType,
}) => {
  const { currentView } = useView();

  const handlePropertyClick = (property: PropertyDataInterface) => {
    if (onPropertyClick) {
      onPropertyClick?.(property);
    } else {
      window.open(
        normalizeUrl(
          `${process.env.NEXT_PUBLIC_WEB_URL}/${property?.propertyType}/${property?.listingId}`,
        ),
        "_blank",
      );
    }
  };

  const handleRequestImagesClick = (property: PropertyDataInterface) => {
    onRequestImagesClick?.(property);
  };

  const listViewModeContainerClasses =
    currentView === "grid"
      ? isFilterOpen
        ? "grid gap-4 grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
        : "grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      : "flex flex-col space-y-4";

  return (
    <div className={"lg:max-w-[1240px] 2xl:max-w-[1340px] w-full mx-auto mt-4"}>
      <section className={listViewModeContainerClasses}>
        {allPropertiesData?.map((property: any, index: number) => (
          <PropertyListCardContainer
            key={`PropertyListCardContainer__${property?.uuid}_${index}`}
            property={property}
            viewMode={currentView}
            onCardClick={handlePropertyClick}
            onRequestImagesClick={() => handleRequestImagesClick(property)}
          />
        ))}
        {currentView === "grid" &&
        isFetchingNextPage &&
        allPropertiesData?.length > 0 ? (
          <SpinnerLoader />
        ) : null}
      </section>

      {(currentView === "grid" &&
        isFetchingNextPage &&
        allPropertiesData?.length === 0) ||
      (currentView === "list" && isFetchingNextPage) ? (
        <SpinnerLoader />
      ) : null}

      {!isFetchingNextPage &&
      Array.isArray(allPropertiesData) &&
      allPropertiesData?.length === 0 ? (
        <EmptyState propertyType={propertyType} />
      ) : null}
    </div>
  );
};

const SpinnerLoader = () => {
  return (
    <div className="flex justify-center items-center w-full">
      <div className="w-14 h-14 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
    </div>
  );
};

const EmptyState = ({
  propertyType,
}: {
  propertyType: "auction-properties" | "private-properties";
}) => {
  const [isLoading, setIsLoading] = useState(false);
  return (
    <div className="min-h-svh w-full">
      <div className="space-y-2 flex flex-col items-center font-openSans text-brand-textLightGrey text-center mt-6 capitalize tracking-wider">
        <FolderSearch size={100} />
        <p className="text-lg">
          <strong>No properties found</strong>
          <br />
        </p>
        <p className="text-sm">
          {propertyType === "auction-properties"
            ? `Your current filters didn't match any active auctions.`
            : propertyType === "private-properties"
            ? `Your current filters didn't match any private properties.`
            : `Your current filters didn't match any properties.`}
        </p>
        <Button
          className="bg-brand-primaryAction hover:bg-brand-primaryActionHover px-3.5 py-2.5"
          disabled={isLoading}
          onClick={() => {
            setIsLoading(true);
            window.open(
              normalizeUrl(
                `${process.env.NEXT_PUBLIC_WEB_URL}/${propertyType}`,
              ),
              "_self",
            );
            setIsLoading(false);
          }}
        >
          {isLoading
            ? "Processing.."
            : propertyType === "auction-properties"
            ? `View All Auctions`
            : propertyType === "private-properties"
            ? `View All Private Properites`
            : `View All Properties`}
        </Button>
      </div>
    </div>
  );
};
